import React, { useState, useEffect } from 'react'
import { Button, Card, Col, Input, Row, Select, Table, Typography, Checkbox, Divider, List, Tag, Empty } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash, faShoppingCart, faRedo } from '@fortawesome/free-solid-svg-icons'
import shoppingListItemsData from '../utils/shoppingListItems.json'

const { Title, Text } = Typography
const { Option } = Select

const ShoppingList = ({ data, setData, recipes }) => {
  const [generatedItems, setGeneratedItems] = useState([])
  const [selectedItems, setSelectedItems] = useState([])
  const [customItemName, setCustomItemName] = useState('')
  const [customItemQuantity, setCustomItemQuantity] = useState('')
  const [customItemUnit, setCustomItemUnit] = useState('')
  const [customItemCategory, setCustomItemCategory] = useState('')

  // Categories for shopping items
  const categories = shoppingListItemsData.categories
  const units = shoppingListItemsData.units

  // Auto-generate shopping list based on recipes
  useEffect(() => {
    if (recipes.length > 0) {
      generateShoppingList()
    }

    // eslint-disable-next-line
  }, [recipes])

  const generateShoppingList = () => {
    // Collect all ingredients from selected recipes
    const allIngredients = recipes.reduce((acc, recipe) => {
      return [...acc, ...recipe.ingredients]
    }, [])

    // Consolidate ingredients (combine duplicates, summing quantities)
    const consolidatedIngredients = []

    allIngredients.forEach((ingredient) => {
      const existing = consolidatedIngredients.find(
        (item) => item.name.toLowerCase() === ingredient.name.toLowerCase() && item.unit === ingredient.unit
      )

      if (existing) {
        existing.amount += ingredient.amount
      } else {
        consolidatedIngredients.push({
          ...ingredient,
          id: Date.now() + Math.random() // Ensure unique IDs
        })
      }
    })

    // Group by category
    const groupedIngredients = {}

    consolidatedIngredients.forEach((ingredient) => {
      const category = ingredient.category || 'Other'

      if (!groupedIngredients[category]) {
        groupedIngredients[category] = []
      }

      groupedIngredients[category].push(ingredient)
    })

    setGeneratedItems(consolidatedIngredients)
  }

  const handleAddCustomItem = () => {
    if (customItemName && customItemQuantity && customItemUnit && customItemCategory) {
      const newItem = {
        id: Date.now(),
        name: customItemName,
        amount: parseFloat(customItemQuantity),
        unit: customItemUnit,
        category: customItemCategory,
        isCustom: true
      }

      setData([...data, newItem])

      // Reset form
      setCustomItemName('')
      setCustomItemQuantity('')
      setCustomItemUnit('')
      setCustomItemCategory('')
    }
  }

  const handleAddSelectedItems = () => {
    if (selectedItems.length > 0) {
      const itemsToAdd = generatedItems.filter(
        (item) => selectedItems.includes(item.id) && !data.some((existing) => existing.id === item.id)
      )

      setData([...data, ...itemsToAdd])
      setSelectedItems([])
    }
  }

  const handleRemoveItem = (id) => {
    setData(data.filter((item) => item.id !== id))
  }

  const handleSelectAll = () => {
    setSelectedItems(generatedItems.map((item) => item.id))
  }

  const handleDeselectAll = () => {
    setSelectedItems([])
  }

  const columns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Quantity',
      key: 'quantity',
      render: (record) => `${record.amount} ${record.unit}`
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (text) => <Tag color='blue'>{text}</Tag>
    },
    {
      title: 'Source',
      key: 'source',
      render: (record) => (record.isCustom ? 'Custom' : 'Generated')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          danger
          icon={<FontAwesomeIcon icon={faTrash} />}
          onClick={() => handleRemoveItem(record.id)}
          size='small'
        />
      )
    }
  ]

  return (
    <div>
      <Title level={4}>Shopping List Generation</Title>
      <Text>Generate and customize shopping lists based on the selected recipes.</Text>

      <Card style={{ marginTop: 16, marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <Button
                type='primary'
                icon={<FontAwesomeIcon icon={faRedo} />}
                onClick={generateShoppingList}
                disabled={recipes.length === 0}
              >
                Regenerate from Recipes
              </Button>

              <div>
                <Button onClick={handleSelectAll} style={{ marginRight: 8 }}>
                  Select All
                </Button>
                <Button onClick={handleDeselectAll}>Deselect All</Button>
              </div>
            </div>

            <Card title='Suggested Items from Recipes' size='small'>
              <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                {generatedItems.length === 0 ? (
                  <Empty description='No items generated. Add recipes first.' />
                ) : (
                  categories.map((category) => {
                    const categoryItems = generatedItems.filter((item) => item.category === category)

                    if (categoryItems.length === 0) return null

                    return (
                      <div key={category} style={{ marginBottom: 16 }}>
                        <Title level={5}>{category}</Title>
                        <List
                          size='small'
                          dataSource={categoryItems}
                          renderItem={(item) => (
                            <List.Item>
                              <Checkbox
                                checked={selectedItems.includes(item.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedItems([...selectedItems, item.id])
                                  } else {
                                    setSelectedItems(selectedItems.filter((id) => id !== item.id))
                                  }
                                }}
                              >
                                {item.amount} {item.unit} {item.name}
                              </Checkbox>
                            </List.Item>
                          )}
                        />
                      </div>
                    )
                  })
                )}
              </div>

              <Button
                type='primary'
                icon={<FontAwesomeIcon icon={faShoppingCart} />}
                onClick={handleAddSelectedItems}
                disabled={selectedItems.length === 0}
                style={{ marginTop: 16 }}
              >
                Add Selected Items to Shopping List
              </Button>
            </Card>
          </Col>

          <Divider>Or add custom items</Divider>

          <Col xs={24} md={8}>
            <Text strong>Item Name</Text>
            <Input
              placeholder='Enter item name'
              value={customItemName}
              onChange={(e) => setCustomItemName(e.target.value)}
            />
          </Col>

          <Col xs={24} md={8}>
            <Text strong>Quantity</Text>
            <Input
              type='number'
              placeholder='Enter quantity'
              value={customItemQuantity}
              onChange={(e) => setCustomItemQuantity(e.target.value)}
              min={0}
              step={0.1}
            />
          </Col>

          <Col xs={12} md={4}>
            <Text strong>Unit</Text>
            <Select
              placeholder='Select unit'
              style={{ width: '100%' }}
              value={customItemUnit}
              onChange={(value) => setCustomItemUnit(value)}
            >
              {units.map((unit) => (
                <Option key={unit} value={unit}>
                  {unit}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={12} md={4}>
            <Text strong>Category</Text>
            <Select
              placeholder='Select category'
              style={{ width: '100%' }}
              value={customItemCategory}
              onChange={(value) => setCustomItemCategory(value)}
            >
              {categories.map((category) => (
                <Option key={category} value={category}>
                  {category}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={24}>
            <Button
              type='primary'
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleAddCustomItem}
              disabled={!customItemName || !customItemQuantity || !customItemUnit || !customItemCategory}
            >
              Add Custom Item
            </Button>
          </Col>
        </Row>
      </Card>

      <Table dataSource={data} columns={columns} rowKey='id' pagination={false} size='small' />
    </div>
  )
}

export default ShoppingList
